import TransformPoints from 'components/plateviewer/well/TransformPoints';
import { LightPathDrawing, RulerDrawing } from './constants';
import { IFOVSizeDTO } from 'business/models/Models';
import { Coordinates, RGBColor } from 'constants/';

export const scaledRegionScibblePoints = (
  points: number[],
  startX: number,
  startY: number,
  tpObject: TransformPoints,
) => {
  const scaledPoints = [];
  for (let i = 0; i < points.length; i += 2) {
    const [xDash, yDash] = tpObject.FromDbToCanvas(points[i], points[i + 1]);
    scaledPoints.push(xDash - startX);
    scaledPoints.push(yDash - startY);
  }
  return scaledPoints;
};

export const getRulerSizeText = (
  localRegion: RulerDrawing,
  tpObject: TransformPoints,
) => {
  let x1 = localRegion.x1;
  let x2 = localRegion.x2;
  let y1 = localRegion.y1;
  let y2 = localRegion.y2;

  if (localRegion.id) {
    const startingPoints = tpObject.FromCanvasToDb(localRegion.x1, localRegion.y1);
    x1 = startingPoints[0];
    y1 = startingPoints[1];
    const endingPoints = tpObject.FromCanvasToDb(localRegion.x2, localRegion.y2);
    x2 = endingPoints[0];
    y2 = endingPoints[1];
  }
  const distance = tpObject.calculateEuclideanDistance(x1, y1, x2, y2);
  if (distance > 1000) {
    return Math.round(distance / 1000) + 'mm';
  }
  return Math.round(distance) + 'µm';
};

const getMinSizeOfLightPath = (lightPath: LightPathDrawing) => {
  if (lightPath.validSizeList.length > 0) {
    return lightPath.validSizeList[0];
  }

  return lightPath.minFOVSize;
};

const getMaxSizeOfLightPath = (lightPath: LightPathDrawing) => {
  if (lightPath.validSizeList.length > 0) {
    return lightPath.validSizeList[lightPath.validSizeList.length - 1];
  }

  return lightPath.maxFOVSize;
};

export const getDiscreteLightPathSize = (
  lightPath: LightPathDrawing,
  width: number,
  height: number,
) => {
  let size: IFOVSizeDTO = {
    height: lightPath.minFOVSize.height,
    width: lightPath.minFOVSize.width,
  };
  if (
    lightPath.sizeFactorList == null ||
    lightPath.sizeFactorList.length == 0 ||
    lightPath.isDiscrete == false
  ) {
    size = lightPath.minFOVSize;
  }

  if (lightPath.validSizeList.length == 1) {
    size = lightPath.validSizeList[0];
  }

  const minSize = getMinSizeOfLightPath(lightPath);
  const maxSize = getMaxSizeOfLightPath(lightPath);

  if (width < minSize.width && height < minSize.height) {
    size = minSize;
  } else if (width > maxSize.width && height > maxSize.height) {
    size = maxSize;
  } else {
    size = maxSize;
  }

  for (let i = 0; i < lightPath.validSizeList.length - 1; i++) {
    const validSize1 = lightPath.validSizeList[i];
    const validSize2 = lightPath.validSizeList[i + 1];

    if (
      (width > validSize1.width && width < validSize2.width) ||
      (height > validSize1.height && height < validSize2.height)
    ) {
      size = validSize2;
    }
  }
  lightPath.rectangleF.width = size.width;
  lightPath.rectangleF.height = size.height;

  return lightPath;
};

const GetValidValue = (value: number, minRange: number, maxRange: number) => {
  const absValue = Math.abs(value);
  if (value == 0) {
    return minRange;
  }
  if (absValue < minRange) {
    return minRange * Math.sign(value);
  }
  if (absValue > maxRange) {
    return maxRange * Math.sign(value);
  }
  return value;
};

const GetAspectRatioHeight = (
  currentWidth: number,
  minFOVWidth: number,
  minFOVHeight: number,
) => {
  return currentWidth * (minFOVHeight / minFOVWidth);
};

const GetAspectRatioWidth = (
  currentHeight: number,
  minFOVWidth: number,
  minFOVHeight: number,
) => {
  return currentHeight * (minFOVWidth / minFOVHeight);
};

export const getContinuousLightPathSize = (
  _lightPath: LightPathDrawing,
  width: number,
  height: number,
) => {
  let size: IFOVSizeDTO;
  const lightPath = _lightPath;
  const newWidth = GetValidValue(
    width,
    lightPath.minFOVSize.width,
    lightPath.maxFOVSize.width,
  );
  const widthSize: IFOVSizeDTO = {
    width: newWidth,
    height: GetAspectRatioHeight(
      newWidth,
      lightPath.minFOVSize.width,
      lightPath.minFOVSize.height,
    ),
  };

  const newHeight = GetValidValue(
    height,
    lightPath.minFOVSize.height,
    lightPath.maxFOVSize.height,
  );
  const heightSize: IFOVSizeDTO = {
    height: newHeight,
    width: GetAspectRatioWidth(
      newHeight,
      lightPath.minFOVSize.width,
      lightPath.minFOVSize.height,
    ),
  };

  if (heightSize.height * heightSize.width > widthSize.height * widthSize.width) {
    size = heightSize;
  } else {
    size = widthSize;
  }

  lightPath.rectangleF.width = size.width;
  lightPath.rectangleF.height = size.height;
  return lightPath;
};

export const getCanvasPointerPosition = (e): Coordinates => {
  const stage = e.target.getStage();
  if (!stage) {
    return;
  }
  return getStagePointerPoints(stage);
};
export const getStagePointerPoints = (stage): Coordinates => {
  const pointerPos = stage.getPointerPosition();
  if (!pointerPos || !stage) {
    return { x: 0, y: 0 };
  }
  const scale = stage.scaleX();
  const newPoint = {
    x: (pointerPos.x - stage.x()) / scale,
    y: (pointerPos.y - stage.y()) / scale,
  };
  return newPoint;
};

export const convertPointsFromCanvasToDB = (
  x: number,
  y: number,
  regionPoints: number[],
  tpObject: TransformPoints,
) => {
  if (!regionPoints) {
    return [];
  }

  return regionPoints.reduce((convertedPoints, currentValue, index, array) => {
    if (index % 2 === 0) {
      const [newx, newy] = tpObject.FromCanvasToDb(
        currentValue + x,
        array[index + 1] + y,
      );
      convertedPoints.push(newx, newy);
    }
    return convertedPoints;
  }, []);
};

export const getColorNumbersString = (drawingColor: RGBColor | string) => {
  if (typeof drawingColor === 'string' && drawingColor?.includes('rgb')) {
    return drawingColor.replace(/[^\d,]/g, '');
  } else {
    const color = drawingColor as RGBColor;
    return `${color.r},${color.g},${color.b}`;
  }
};

export const getRgbColorString = (drawingColor: RGBColor | string): string => {
  if (typeof drawingColor === 'string' && drawingColor.includes('rgb')) {
    return drawingColor;
  } else if (typeof drawingColor === 'string' && !drawingColor.includes('rgb')) {
    return `rgb(${drawingColor})`;
  } else {
    const color = drawingColor as RGBColor;
    return `rgb(${color.r},${color.g},${color.b})`;
  }
};

export const buildCrystalName = ({
  template,
  plateID,
  wellNum,
  dropNumber,
  expName,
  projectName,
  index,
}) => {
  const paddedIndex = String(index).padStart(2, '0');
  if (template == '(none)') {
    return 'Crystal' + paddedIndex;
  }
  let name: string = template
    .replace('<PlateID>', String(plateID))
    .replace('<Well>', String(wellNum))
    .replace('<Drop>', String(dropNumber))
    .replace('<ExperimentName>', expName)
    .replace(/_Crystal\d+$/, `_Crystal${paddedIndex}`);

  if (template.includes('<ProjectName>')) {
    name = name.replace('<ProjectName>', projectName || '');
  }

  return name;
};
